/**
 * useWorkspaceUIState Hook
 * 
 * Manages all UI state for the WorkspaceSettings page:
 * - Dialog open/close states
 * - Form input states
 * - Loading/processing states
 * - Snackbar states
 */

import { useState, useRef } from "react";
import type React from "react";
import { WorkspaceMemberWithDetails, WorkspaceRole } from "../../../types";

export interface UseWorkspaceUIStateReturn {
  // Workspace info editing
  saving: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  uploadingLogo: boolean;
  setUploadingLogo: React.Dispatch<React.SetStateAction<boolean>>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;

  // Invite dialog
  inviteDialogOpen: boolean;
  setInviteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  inviteEmail: string;
  setInviteEmail: React.Dispatch<React.SetStateAction<string>>;
  inviteRole: WorkspaceRole;
  setInviteRole: React.Dispatch<React.SetStateAction<WorkspaceRole>>;
  inviting: boolean;
  setInviting: React.Dispatch<React.SetStateAction<boolean>>;
  inviteError: string;
  setInviteError: React.Dispatch<React.SetStateAction<string>>;
  createdInvitationLink: string | null;
  setCreatedInvitationLink: React.Dispatch<React.SetStateAction<string | null>>;
  invitationSuccessDialogOpen: boolean;
  setInvitationSuccessDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  emailSent: boolean;
  setEmailSent: React.Dispatch<React.SetStateAction<boolean>>;
  emailError: { type: string; message: string } | undefined;
  setEmailError: React.Dispatch<React.SetStateAction<{ type: string; message: string } | undefined>>;

  // Member role editing
  changingRole: string | null;
  setChangingRole: React.Dispatch<React.SetStateAction<string | null>>;

  // Delete workspace dialog
  deleteDialogOpen: boolean;
  setDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  deleting: boolean;
  setDeleting: React.Dispatch<React.SetStateAction<boolean>>;
  deleteWorkspaceName: string;
  setDeleteWorkspaceName: React.Dispatch<React.SetStateAction<string>>;
  allowNavigationRef: React.MutableRefObject<boolean>;

  // Remove logo dialog
  removeLogoDialogOpen: boolean;
  setRemoveLogoDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Remove member dialog
  removeMemberDialogOpen: boolean;
  setRemoveMemberDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  memberToRemove: WorkspaceMemberWithDetails | null;
  setMemberToRemove: React.Dispatch<React.SetStateAction<WorkspaceMemberWithDetails | null>>;
  removing: boolean;
  setRemoving: React.Dispatch<React.SetStateAction<boolean>>;

  // Leave workspace dialog
  leaveDialogOpen: boolean;
  setLeaveDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  leaving: boolean;
  setLeaving: React.Dispatch<React.SetStateAction<boolean>>;
  leaveWorkspaceName: string;
  setLeaveWorkspaceName: React.Dispatch<React.SetStateAction<string>>;

  // Resend invitation
  resendingInvite: string | null;
  setResendingInvite: React.Dispatch<React.SetStateAction<string | null>>;

  // Cleanup orphaned members
  cleaningUp: boolean;
  setCleaningUp: React.Dispatch<React.SetStateAction<boolean>>;
  cleanupOrphanedDialogOpen: boolean;
  setCleanupOrphanedDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Snackbar for demo mode alerts
  snackbarOpen: boolean;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  snackbarMessage: string;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<string>>;
  snackbarElement: React.ReactElement | null;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
}

/**
 * Hook to manage all UI state for WorkspaceSettings page
 */
export function useWorkspaceUIState(): UseWorkspaceUIStateReturn {
  // Workspace info editing
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Invite dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>("");
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>("user");
  const [inviting, setInviting] = useState<boolean>(false);
  const [inviteError, setInviteError] = useState<string>("");
  const [createdInvitationLink, setCreatedInvitationLink] = useState<string | null>(null);
  const [invitationSuccessDialogOpen, setInvitationSuccessDialogOpen] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<{ type: string; message: string } | undefined>(undefined);

  // Member role editing
  const [changingRole, setChangingRole] = useState<string | null>(null);

  // Delete workspace dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteWorkspaceName, setDeleteWorkspaceName] = useState<string>("");
  // Use ref for allowNavigation since it needs to be checked synchronously in beforeunload
  const allowNavigationRef = useRef<boolean>(false);

  // Remove logo dialog
  const [removeLogoDialogOpen, setRemoveLogoDialogOpen] = useState<boolean>(false);

  // Remove member dialog
  const [removeMemberDialogOpen, setRemoveMemberDialogOpen] = useState<boolean>(false);
  const [memberToRemove, setMemberToRemove] = useState<WorkspaceMemberWithDetails | null>(null);
  const [removing, setRemoving] = useState<boolean>(false);

  // Leave workspace dialog
  const [leaveDialogOpen, setLeaveDialogOpen] = useState<boolean>(false);
  const [leaving, setLeaving] = useState<boolean>(false);
  const [leaveWorkspaceName, setLeaveWorkspaceName] = useState<string>("");

  // Resend invitation
  const [resendingInvite, setResendingInvite] = useState<string | null>(null);

  // Cleanup orphaned members
  const [cleaningUp, setCleaningUp] = useState<boolean>(false);
  const [cleanupOrphanedDialogOpen, setCleanupOrphanedDialogOpen] = useState<boolean>(false);

  // Snackbar for demo mode alerts
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarElement, setSnackbarElement] = useState<React.ReactElement | null>(null);

  return {
    // Workspace info editing
    saving,
    setSaving,
    uploadingLogo,
    setUploadingLogo,
    fileInputRef,

    // Invite dialog
    inviteDialogOpen,
    setInviteDialogOpen,
    inviteEmail,
    setInviteEmail,
    inviteRole,
    setInviteRole,
    inviting,
    setInviting,
    inviteError,
    setInviteError,
    createdInvitationLink,
    setCreatedInvitationLink,
    invitationSuccessDialogOpen,
    setInvitationSuccessDialogOpen,
    emailSent,
    setEmailSent,
    emailError,
    setEmailError,

    // Member role editing
    changingRole,
    setChangingRole,

    // Delete workspace dialog
    deleteDialogOpen,
    setDeleteDialogOpen,
    deleting,
    setDeleting,
    deleteWorkspaceName,
    setDeleteWorkspaceName,
    allowNavigationRef,

    // Remove logo dialog
    removeLogoDialogOpen,
    setRemoveLogoDialogOpen,

    // Remove member dialog
    removeMemberDialogOpen,
    setRemoveMemberDialogOpen,
    memberToRemove,
    setMemberToRemove,
    removing,
    setRemoving,

    // Leave workspace dialog
    leaveDialogOpen,
    setLeaveDialogOpen,
    leaving,
    setLeaving,
    leaveWorkspaceName,
    setLeaveWorkspaceName,

    // Resend invitation
    resendingInvite,
    setResendingInvite,

    // Cleanup orphaned members
    cleaningUp,
    setCleaningUp,
    cleanupOrphanedDialogOpen,
    setCleanupOrphanedDialogOpen,

    // Snackbar
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    setSnackbarMessage,
    snackbarElement,
    setSnackbarElement,
  };
}
